'use client';

import { useState, Suspense } from 'react';
import { Loader2 } from 'lucide-react';
import Link from 'next/link';
import { createClient } from '@/lib/supabase/client';
import { identifyProductUser, recordProductEvent } from '@/lib/product-analytics';
import '@/styles/brutalist/forwarder-login.css';

function ShipperSignupForm() {
  const [companyName, setCompanyName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    setIsLoading(true);

    try {
      const res = await fetch('/api/shipper/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ company_name: companyName, email, password }),
      });

      const data = await res.json();

      if (!res.ok) {
        if (data.error === 'email_already_registered') {
          setError('An account with this email already exists. Try logging in instead.');
        } else {
          setError(data.error ?? 'Signup failed. Please try again.');
        }
        return;
      }

      identifyProductUser(data.id, 'shipper');
      recordProductEvent('sign_up', { account_type: 'shipper', method: 'email' }, { posthog: false });
      // User created — now establish a browser session so middleware lets us through
      const supabase = createClient();
      const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
      if (signInError) {
        // Account exists but session failed — send to login with prefilled destination
        // A full navigation initializes the server session after sign-in.
        // eslint-disable-next-line @next/next/no-location-assign-relative-destination
        window.location.href = '/login?redirect=%2Fdashboard%2Fmy-shipments%3Fwelcome%3Dtrue';
        return;
      }

      // A full navigation initializes the server session after sign-in.
      // eslint-disable-next-line @next/next/no-location-assign-relative-destination
      window.location.href = '/dashboard/my-shipments?welcome=true';
    } catch {
      setError('Signup failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-logo">
        <div className="login-logo-text">
          <span style={{ fontSize: '24px' }}>&raquo;</span> Shipzy
        </div>
      </div>

      <div className="login-header">
        <h1 className="login-title">Create Shipper Account</h1>
        <p className="login-description">
          Track your shipments, get delay alerts, and monitor arrivals.
          Free accounts save up to 3 BLs.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="login-form">
        <div className="login-form-field">
          <label htmlFor="company_name" className="login-form-label">
            Company Name
          </label>
          <input
            id="company_name"
            type="text"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            placeholder="Your Company Ltd"
            required
            disabled={isLoading}
            className="login-form-input"
          />
        </div>

        <div className="login-form-field">
          <label htmlFor="email" className="login-form-label">
            Email
          </label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your.email@company.com"
            required
            disabled={isLoading}
            className="login-form-input"
          />
        </div>

        <div className="login-form-field">
          <label htmlFor="password" className="login-form-label">
            Password
          </label>
          <input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Min. 8 characters"
            required
            disabled={isLoading}
            className="login-form-input"
          />
        </div>

        <div className="login-form-field">
          <label htmlFor="confirm_password" className="login-form-label">
            Confirm Password
          </label>
          <input
            id="confirm_password"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Repeat password"
            required
            disabled={isLoading}
            className="login-form-input"
          />
        </div>

        {error && (
          <div className="login-error">
            <p className="login-error-text">{error}</p>
          </div>
        )}

        <button type="submit" className="login-button" disabled={isLoading}>
          {isLoading ? (
            <>
              <Loader2 className="login-button-icon" />
              Creating account…
            </>
          ) : (
            'Create Account'
          )}
        </button>

        <div className="login-footer">
          Already have an account?{' '}
          <Link href="/login" className="login-footer-link">
            Log in
          </Link>
        </div>
      </form>
    </div>
  );
}

export default function ShipperSignupPage() {
  return (
    <div className="forwarder-login-page">
      <Suspense
        fallback={
          <div className="login-container">
            <div className="login-logo">
              <div className="login-logo-text">
                <span style={{ fontSize: '24px' }}>&raquo;</span> Shipzy
              </div>
            </div>
            <div className="login-success">
              <Loader2 className="login-button-icon" style={{ width: 32, height: 32, margin: '0 auto' }} />
            </div>
          </div>
        }
      >
        <ShipperSignupForm />
      </Suspense>
    </div>
  );
}
